import {
  almanacsCustomerReadStatement,
  plantProfilesTemplatesUpdateStatement,
} from "portal/views/plantProfiles/permissions";
import { buildPermission } from "portal/utils/auth";
import { ComponentType, FC, ReactNode } from "react";
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import {
  readAllCustomersStatement,
  readOwnCustomerStatement,
} from "portal/views/customers/permissions";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { withFeatureFlag } from "portal/components/RequireFeatureFlag";

// List categories also runs ResolveCrossCustomerReadAccess in the BE, which requires
// read:customers
const customerReadStatement = {
  or: [readOwnCustomerStatement, readAllCustomersStatement],
};

export const categoryTemplatesReadStatement = {
  or: [
    buildPermission(
      PermissionAction.read,
      PermissionResource.plant_profile_cat_template,
      PermissionDomain.customer
    ),
    buildPermission(
      PermissionAction.read,
      PermissionResource.plant_profile_cat_template,
      PermissionDomain.all
    ),
  ],
};

export const categoryTemplatesUpdateStatement = {
  or: [
    buildPermission(
      PermissionAction.update,
      PermissionResource.plant_profile_cat_template,
      PermissionDomain.customer
    ),
    buildPermission(
      PermissionAction.update,
      PermissionResource.plant_profile_cat_template,
      PermissionDomain.all
    ),
  ],
};

const categoryTemplatesPageReadStatement = {
  and: [
    categoryTemplatesReadStatement,
    almanacsCustomerReadStatement,
    customerReadStatement,
  ],
};

export const useCanUpdateCategoryTemplate = (): boolean =>
  useAuthorizationRequired(categoryTemplatesUpdateStatement);

export const useCanUpdateCarbonCategoryTemplates = (): boolean =>
  useAuthorizationRequired(plantProfilesTemplatesUpdateStatement);

export const useCanCustomerReadCategoryTemplates = (
  customerUuid?: string
): boolean => {
  const { isEnabled: hasCustomCategories } = useFeatureFlagForCustomer(
    FeatureFlag.CUSTOM_CATEGORIES,
    customerUuid
  );
  const isCategoryTemplatesAuthorized = useAuthorizationRequired(
    categoryTemplatesReadStatement
  );
  return Boolean(isCategoryTemplatesAuthorized && hasCustomCategories);
};

export const useCanSeeCategoryTemplatesPage = (): boolean => {
  const { isEnabled: hasCustomCategories } = useFeatureFlag(
    FeatureFlag.CUSTOM_CATEGORIES
  );
  const isCategoryTemplatesAuthorized = useAuthorizationRequired(
    categoryTemplatesPageReadStatement
  );
  return Boolean(isCategoryTemplatesAuthorized && hasCustomCategories);
};

export const withCategoryTemplatesReadAccess = <P extends object>(
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FC<P> =>
  withAuthenticationRequired(
    withFeatureFlag(
      { flag: FeatureFlag.CUSTOM_CATEGORIES, noFlag: Path.FLEET },
      withAuthorizationRequired(
        categoryTemplatesPageReadStatement,
        Component,
        otherwise
      )
    )
  );

export const useCanUpdateCategoryTemplatesPage = (): boolean => {
  const { isEnabled: hasCustomCategories } = useFeatureFlag(
    FeatureFlag.CUSTOM_CATEGORIES
  );
  const isCategoryTemplatesAuthorized = useAuthorizationRequired(
    categoryTemplatesUpdateStatement
  );
  return Boolean(isCategoryTemplatesAuthorized && hasCustomCategories);
};

export const withCategoryTemplatesUpdateAccess = <P extends object>(
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FC<P> =>
  withAuthenticationRequired(
    withFeatureFlag(
      { flag: FeatureFlag.CUSTOM_CATEGORIES, noFlag: Path.FLEET },
      withAuthorizationRequired(
        categoryTemplatesUpdateStatement,
        Component,
        otherwise
      )
    )
  );
